import { QueryKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/queries/api-client'
import { Physician, PhysicianSchema, Provider, ProviderListItem, ProviderListItemSchema, ProviderSchema } from '@/types/provider'

export const keys = {
  locations: (): QueryKey => ['locations'],
  providers: (locations?: string[]): QueryKey => {
    return locations ? ['providers', locations] : ['providers']
  },
  allProviders: (orderBy?: string, sortOrder?: string): QueryKey => {
    return orderBy && sortOrder ? ['allProviders', orderBy, sortOrder] : ['allProviders']
  },
  physicians: (providerId: number): QueryKey => ['providers', providerId, 'physicians'],
}

export function useLocations() {
  return useQuery({
    queryKey: keys.locations(),
    queryFn: async () => {
      const res = await apiClient.get('locations')
      return res.data.locations as string[]
    },
  })
}

export function useProviders(locations?: string[]) {
  return useQuery({
    queryKey: keys.providers(locations),
    queryFn: async () => {
      const res = await apiClient.get('providers', {
        params: {
          locations: locations && locations.length ? locations.join(',') : undefined,
        },
      })
      return res.data.providers.map((item: any) => ProviderSchema.parse(item)) as Provider[]
    },
  })
}

export function useAllProviders(orderBy?: string, sortOrder?: string) {
  return useQuery({
    queryKey: keys.allProviders(orderBy, sortOrder),
    queryFn: async () => {
      const res = await apiClient.get('providers/all', { params: { orderBy, sortOrder } })
      return res.data.providers.map((item: any) => ProviderListItemSchema.parse(item)) as ProviderListItem[]
    },
  })
}

export function useCreateProvider() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ name, state, procedure, executive, staff, rate, classification, requiresPhysician }: {
      name: string
      state: string
      procedure: string
      executive: string
      staff: string
      rate: string
      classification: string
      requiresPhysician: boolean
    }) => {
      return apiClient.post('providers', { name, state, procedure, executive, staff, rate, classification, requiresPhysician })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.locations() })
      queryClient.invalidateQueries({ queryKey: keys.providers() })
      queryClient.invalidateQueries({ queryKey: keys.allProviders() })
    },
  })
}

export function useProviderPhysicians(providerId: number) {
  return useQuery({
    queryKey: keys.physicians(providerId),
    queryFn: async () => {
      const res = await apiClient.get(`providers/${providerId}/physicians`)
      return res.data.physicians.map((item: any) => PhysicianSchema.parse(item)) as Physician[]
    },
  })
}

export function useCreatePhysician() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ providerId, name }: { providerId: number, name: string }) => {
      const res = await apiClient.post(`providers/${providerId}/physicians`, { name })
      return PhysicianSchema.parse(res.data.physician)
    },
    onSuccess: (_physician, { providerId }) => {
      queryClient.invalidateQueries({ queryKey: keys.physicians(providerId) })
    },
  })
}

export function useUpdateProvider() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, name, state, procedure, executive, staff, rate, classification, requiresPhysician }: {
      id: number
      name: string
      state: string
      procedure: string
      executive: string
      staff: string
      rate: string
      classification: string
      requiresPhysician: boolean
    }) => {
      return apiClient.patch(`providers/${id}`, { name, state, procedure, executive, staff, rate, classification, requiresPhysician })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.locations() })
      queryClient.invalidateQueries({ queryKey: keys.providers() })
      queryClient.invalidateQueries({ queryKey: keys.allProviders() })
    },
  })
}
